// src/components/SuperAdminDashboard.js
import { appTypography } from "../theme/appTypography";
import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import {
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Drawer,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Tooltip,
  Chip,
  TextField,
  Alert,
  Snackbar,
  Avatar,
  Grid, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  Badge,
  Stack,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  MailOutline as MailOutlineIcon,
  PeopleAlt as PeopleAltIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  QueryStats as QueryStatsIcon,
  Logout as LogoutIcon,
  NotificationsActive as NotificationsActiveIcon,
  Send as SendIcon,
  TaskAlt as TaskAltIcon,
  Radar as RadarIcon
} from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  motion,
  AnimatePresence,
  animate,
  useMotionValue,
} from "framer-motion";
import { useNavigate } from "react-router-dom";

import { AuthContext } from "../AuthContext";
import api from "../axiosConfig";
import Performance from "./Perfomance";

import { db as firestoreDb } from "../firebase";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  addDoc,
  getDocs,
  where,
} from "firebase/firestore";

import AICoachWidget from "./Marketing/AICoachWidget"; 
import useAgencyWorker from "../hooks/useAgencyWorker";
import BroadcastBudgetPanel from "./BroadcastBudgetPanel";

// ---------------- THEME -------------
const superAdminTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#2db838",
      light: "#66bb6a",
      dark: "#0e6346",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#64748b",
      light: "#94a3b8",
      dark: "#475569",
      contrastText: "#ffffff",
    },
    background: {
      default: "#f5f7fb",
      paper: "#ffffff",
    },
    text: {
      primary: "#111827",
      secondary: "#6b7280",
    },
    divider: "rgba(148, 163, 184, 0.4)",
    success: { main: "#22c55e", dark: "#15803d" },
    warning: { main: "#f59e0b" },
    error: { main: "#ef4444" },
    info: { main: "#0ea5e9" },
  },
  typography: {
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600, borderRadius: 999 },
    ...appTypography,
  },
  shape: { borderRadius: 16 },
  components: {
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: "1px solid rgba(148,163,184,0.25)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          backgroundColor: "#ffffff",
          borderBottom: "1px solid rgba(148,163,184,0.3)",
          color: "#111827",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "none",
          boxShadow: "2px 0 20px rgba(15,23,42,0.08)",
          background: "linear-gradient(180deg,#ffffff,#f9fafb)",
        },
      },
    },
  },
});

// --------- LAYOUT CONSTANTS ----------
const DRAWER_WIDTH = 260;
const HEADER_HEIGHT = 72;

// --------- HELPERS ----------
const formatDateTime = (value) => {
  if (!value) return "N/A";
  const d =
    value?.toDate?.() instanceof Date
      ? value.toDate()
      : value instanceof Date
      ? value
      : new Date(value);
  if (Number.isNaN(d.getTime())) return "N/A";
  return (
    d.toLocaleDateString() +
    " " +
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
};

const AnimatedNumber = ({ value, duration = 1.4 }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const controls = animate(0, value || 0, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.floor(v)),
    });
    return () => controls.stop();
  }, [value, duration]);

  return (
    <motion.span
      style={{ display: "inline-block", fontVariantNumeric: "tabular-nums" }}
      transition={{ type: "spring" }}
    >
      {display.toLocaleString()}
    </motion.span>
  );
};

const SpeedTestDial = ({ loading, value }) => {
  const needleAngle = useMotionValue(-110);
  const [display, setDisplay] = useState(0);
  const [phase, setPhase] = useState("connecting");

  useEffect(() => {
    let connectingAnim;

    if (loading) {
      setPhase("connecting");
      setDisplay(0);
      connectingAnim = animate(needleAngle, [-110, -90, -105, -110], {
        duration: 3,
        ease: "easeInOut",
        repeat: Infinity,
      });
    } else {
      if (connectingAnim) connectingAnim.stop();
      const safeVal = value || 0;
      const normalized =
        safeVal <= 0
          ? 0
          : Math.log10(safeVal + 1) / Math.log10(100000 + 1);
      const targetAngle = -110 + normalized * 220;

      setPhase("fetching");

      animate(needleAngle, targetAngle, {
        duration: 1.6,
        ease: [0.12, 0.74, 0.3, 1.2],
      });

      animate(0, safeVal, {
        duration: 1.6,
        ease: [0.12, 0.74, 0.3, 1.2],
        onUpdate: (v) => setDisplay(Math.floor(v)),
        onComplete: () => setPhase("done"),
      });
    }

    return () => {
      if (connectingAnim) connectingAnim.stop();
    };
  }, [loading, value, needleAngle]);

  const showNumber = phase === "fetching" || phase === "done";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mt: 0.5,
        width: '100%' 
      }}
    >
      <Box sx={{ position: "relative", width: 130, height: 80 }}>
        <svg
          width="130"
          height="80"
          viewBox="0 0 130 80"
          style={{ position: "absolute", inset: 0 }}
        >
          <path
            d="M10 70 A55 55 0 0 1 120 70"
            stroke="#e5e7eb"
            strokeWidth="7"
            fill="none"
            strokeLinecap="round"
          />
          <path
            d="M20 70 A45 45 0 0 1 110 70"
            stroke="#d1fae5"
            strokeWidth="5"
            fill="none"
            strokeLinecap="round"
          />
        </svg>

        <motion.div
          style={{
            position: "absolute",
            left: "50%",
            bottom: 8,
            width: 3,
            height: 45,
            borderRadius: 999,
            background:
              phase === "connecting"
                ? "#9ca3af"
                : superAdminTheme.palette.success.main,
            transformOrigin: "bottom center",
            rotate: needleAngle,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: 0,
            transform: "translateX(-50%)",
            width: 16,
            height: 16,
            borderRadius: "999px",
            bgcolor: "#ffffff",
            border: "3px solid #d1d5db",
          }}
        />
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, minHeight: 32 }}>
        {showNumber ? display.toLocaleString() : "0"}
      </Typography>

      <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.25, minHeight: 16 }}>
        {phase === "connecting" ? "Connecting…" : phase === "fetching" ? "Fetching…" : "Done"}
      </Typography>
    </Box>
  );
};

const StatCard = ({
  title,
  value,
  displayValue,
  subtitle,
  icon: Icon,
  color = "primary",
  loading,
  onClick,
}) => {
  const isClickable = typeof onClick === 'function';

  return (
    <Paper
      component={motion.div}
      whileHover={isClickable ? { y: -3, boxShadow: "0 20px 40px rgba(15,23,42,0.08)" } : {}}
      transition={{ type: "spring", stiffness: 260, damping: 18 }}
      onClick={isClickable ? onClick : undefined}
      sx={{
        width: "100%",
        p: 2.5,
        borderRadius: 4,
        cursor: isClickable ? "pointer" : "default",
        position: "relative",
        overflow: "hidden",
        height: "100%",
        background:
          color === "primary"
            ? "linear-gradient(135deg,#ecfdf3,#ffffff)"
            : "linear-gradient(135deg,#eff6ff,#ffffff)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -30,
          right: -30,
          width: 120,
          height: 120,
          borderRadius: "999px",
          background:
            color === "primary"
              ? "rgba(34,197,94,0.12)"
              : "rgba(59,130,246,0.12)",
          filter: "blur(10px)",
        }}
      />
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor:
                color === "primary"
                  ? "rgba(34,197,94,0.12)"
                  : "rgba(59,130,246,0.12)",
              mr: 1.5,
            }}
          >
            {Icon && (
              <Icon
                fontSize="small"
                sx={{
                  color: color === "primary" ? "success.main" : "info.main",
                }}
              />
            )}
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {title}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 0.5, color: "text.primary" }}
          >
            {displayValue ?? <AnimatedNumber value={value || 0} />}
          </Typography>
        )}

        {subtitle && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// ---------------- MAIN COMPONENT ----------------
function SuperAdminDashboardInner() {
  const { user, logout, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(superAdminTheme.breakpoints.up("sm"));

  // layout
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [selectedMenu, setSelectedMenu] = useState("Command Center");
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  // stats
  const [googleContactsCount, setGoogleContactsCount] = useState(0);
  const [gcLoading, setGcLoading] = useState(true);
  const [hubspotContactsCount, setHubspotContactsCount] = useState(0);
  const [hubspotLoading, setHubspotLoading] = useState(true);
  const [totalInquiries, setTotalInquiries] = useState(0);
  const [overdueInquiriesCount, setOverdueInquiriesCount] = useState(0);
  const [globalResponseRate, setGlobalResponseRate] = useState(null);
  const [emailEngagement, setEmailEngagement] = useState({
    sent: 0,
    opened: 0,
    clicked: 0,
    openRate: 0,
    clickRate: 0,
  });
  const [emailEngagementLoading, setEmailEngagementLoading] = useState(true);
  const [broadcastBudget, setBroadcastBudget] = useState({ email: null, whatsapp: null });
  const [broadcastBudgetLoading, setBroadcastBudgetLoading] = useState(true);

  // email campaign stats
  const [emailSenders, setEmailSenders] = useState([]);
  const [emailStatsLoading, setEmailStatsLoading] = useState(true);

  // inquiries
  const [allInquiries, setAllInquiries] = useState([]);
  const [loadingInquiries, setLoadingInquiries] = useState(true);

  // tasks
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [newTaskTarget, setNewTaskTarget] = useState("consultants");
  const [newTaskAssigneeEmail, setNewTaskAssigneeEmail] = useState("");
  const [newTaskRelatedInquiryId, setNewTaskRelatedInquiryId] = useState("");

  // pools
  const [consultants, setConsultants] = useState([]);
  const [marketingUsers, setMarketingUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // assigning inquiries
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [selectedConsultantEmail, setSelectedConsultantEmail] = useState("");

  const { alerts } = useAgencyWorker({
    workerPath: '/agency.worker.js',
    session: { 
      role: 'superadmin',
      userId: user?.uid 
    }
  });

  // [FIX] REMOVED REDUNDANT EFFECT:
  // The AICoachWidget handles data updates internally now.
  // Removing the manual updateData() effect prevents the infinite loop.

  // [FIX] MOBILE SAFE NOTIFICATION LOGIC
  useEffect(() => {
    try {
        const isMuted = localStorage.getItem('agency_mute') === 'true';
        if (alerts.length === 0 || isMuted) return;

        const critical = alerts.find(a => a.severity === 'critical' || a.severity === 'high' || a.severity === 'ghost');
        
        // SAFE GUARD: Check if window exists and Notification API is available
        if (critical && typeof window !== "undefined" && "Notification" in window) {
            if (Notification.permission === "granted") {
                try {
                    const notif = new Notification("AGENCY COMMAND", { 
                        body: critical.message, 
                        icon: '/favicon.ico', 
                        tag: critical.id, 
                        requireInteraction: true 
                    });
                    setTimeout(() => notif.close(), 10000);
                } catch (e) {
                    console.warn("Notification error (mobile restriction):", e);
                }
            }
        }
    } catch (e) {
        console.warn("Notification logic error:", e);
    }
  }, [alerts]);

  const handleWidgetAssignClick = (alert) => {
      // [FIX] Updated Regex to capture all prefixes
      const rawId = alert.id.replace(/^(new_|hot_|urgent_|ghost_|urgent_unclaimed_|hot_tracking_|lead_|opportunity_)/, '');
      const inquiry = allInquiries.find(i => String(i.id) === String(rawId));
      
      if (inquiry) {
          handleOpenAssignDialog(inquiry);
      } else {
          setSnackbarMsg("Inquiry details not found locally.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
      }
  };

  const notificationsCount = useMemo(() => {
    const workerAlerts = alerts.length;
    const overdueCount = overdueInquiriesCount || 0;
    const openTasks = tasks.filter((t) => t.status !== "completed").length;
    return overdueCount + openTasks + workerAlerts;
  }, [overdueInquiriesCount, tasks, alerts]);

  const displayName = useMemo(() => {
    if (!user) return "SuperAdmin";
    return (
      user.name ||
      user.displayName?.split(" ")[0] ||
      user.email?.split("@")[0] ||
      "SuperAdmin"
    );
  }, [user]);

  const broadcastBudgetChannels = useMemo(() => {
    return [broadcastBudget?.email, broadcastBudget?.whatsapp].filter(Boolean);
  }, [broadcastBudget]);

  useEffect(() => {
    if (!isDesktop) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(true);
    }
  }, [isDesktop]);

  const handleDrawerToggle = () => {
    if (!isDesktop) {
      setDrawerOpen((prev) => !prev);
    }
  };

  // Google contacts count
  useEffect(() => {
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        setGcLoading(true);
        const gcRes = await api.get("/marketing/google-contacts/count");
        if (!cancelled) {
          setGoogleContactsCount(gcRes.data?.total || 0);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching Google contact counts", err);
          setSnackbarMsg("Failed to load Google contact statistics.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } finally {
        if (!cancelled) setGcLoading(false);
      }
    };
    fetchCounts();
    return () => { cancelled = true; };
  }, []);

  // HubSpot contacts count
  useEffect(() => {
    let cancelled = false;
    const loadHubspotCount = async () => {
      try {
        setHubspotLoading(true);
        const res = await api.get("/marketing/hubspot/contacts/count");
        if (!cancelled) {
          setHubspotContactsCount(res.data?.total || 0);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("HubSpot count error", err);
          setSnackbarMsg("Failed to load HubSpot contacts.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } finally {
        if (!cancelled) setHubspotLoading(false);
      }
    };
    loadHubspotCount();
    return () => { cancelled = true; };
  }, []);

  // email stats
  useEffect(() => {
    let cancelled = false;
    const fetchEmailStats = async () => {
      try {
        setEmailStatsLoading(true);
        const res = await api.get("/marketing/email-campaigns/top-senders");
        if (!cancelled) {
          const rows = res.data?.data || res.data || [];
          setEmailSenders(Array.isArray(rows) ? rows : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching email stats", err);
          setSnackbarMsg("Failed to load email campaign statistics.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } finally {
        if (!cancelled) setEmailStatsLoading(false);
      }
    };
    fetchEmailStats();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const fetchEmailEngagement = async () => {
      try {
        setEmailEngagementLoading(true);
        const res = await api.get("/analytics/marketing/sendgrid-summary");
        const sent = Number(res.data?.sent ?? 0);
        const opened = Number(res.data?.opened ?? 0);
        const clicked = Number(res.data?.clicked ?? 0);

        if (!cancelled) {
          setEmailEngagement({
            sent,
            opened,
            clicked,
            openRate: sent > 0 ? Math.round((opened / sent) * 100) : 0,
            clickRate: sent > 0 ? Math.round((clicked / sent) * 100) : 0,
          });
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("Error fetching email engagement summary", err);
        }
      } finally {
        if (!cancelled) setEmailEngagementLoading(false);
      }
    };

    fetchEmailEngagement();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const fetchBroadcastBudget = async () => {
      try {
        setBroadcastBudgetLoading(true);
        const res = await api.get("/analytics/marketing/broadcast-budget");
        const channels = res.data?.data?.channels || res.data?.channels || {};

        if (!cancelled) {
          setBroadcastBudget({
            email: channels.email || null,
            whatsapp: channels.whatsapp || null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("Error fetching broadcast budget", err);
        }
      } finally {
        if (!cancelled) setBroadcastBudgetLoading(false);
      }
    };

    fetchBroadcastBudget();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // users
  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      try {
        setUsersLoading(true);
        const res = await api.get("/admin/users");
        if (!cancelled) {
          const raw = Array.isArray(res.data?.data) && res.data.data.length ? res.data.data : Array.isArray(res.data) ? res.data : [];
          const consultantList = raw.filter((u) => String(u.role || "").toLowerCase() === "consultant");
          const marketingList = raw.filter((u) => String(u.role || "").toLowerCase() === "marketing");
          setConsultants(consultantList);
          setMarketingUsers(marketingList);
        }
      } catch (err) {
        if (!cancelled) console.error("Error loading consultants / marketing users", err);
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };
    loadUsers();
    return () => { cancelled = true; };
  }, []);

  // Firestore: inquiries
  useEffect(() => {
    if (!user) return;
    setLoadingInquiries(true);
    const qInq = query(collection(firestoreDb, "allInquiries"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(qInq, (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAllInquiries(data);
        setLoadingInquiries(false);
      }, (err) => {
        console.error("Error loading inquiries", err);
        setLoadingInquiries(false);
      }
    );
    return () => unsub();
  }, [user]);

  // derive global metrics
  useEffect(() => {
    if (!allInquiries || allInquiries.length === 0) {
      setTotalInquiries(0);
      setOverdueInquiriesCount(0);
      setGlobalResponseRate(null);
      return;
    }
    const total = allInquiries.length;
    const overdue = allInquiries.filter((inq) => inq.isOverdue).length;
    const answered = allInquiries.filter((inq) => {
      const st = inq.status || "";
      const hasConsultant = !!inq.consultantId;
      const notNew = st !== "new_chat" && st !== "pending_consultant" && st !== "pending_assignment";
      return hasConsultant && notNew;
    }).length;
    const responseRate = total > 0 ? (answered / total) * 100 : null;
    setTotalInquiries(total);
    setOverdueInquiriesCount(overdue);
    setGlobalResponseRate(responseRate);
  }, [allInquiries]);

  // tasks
  useEffect(() => {
    if (!user) return;
    setTasksLoading(true);
    const qTasks = query(collection(firestoreDb, "superadminTasks"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(qTasks, (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTasks(data);
        setTasksLoading(false);
      },
      (err) => {
        console.error("Error loading superadminTasks", err);
        setTasksLoading(false);
      }
    );
    return () => unsub();
  }, [user]);

  const handleOpenAssignDialog = useCallback((inquiry) => {
    setSelectedInquiry(inquiry);
    setSelectedConsultantEmail("");
    setAssignDialogOpen(true);
  }, []);

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedInquiry(null);
    setSelectedConsultantEmail("");
  };

  const assignInquiry = async () => {
    if (!selectedInquiry || !selectedInquiry.id || !selectedConsultantEmail) return;
    try {
      const inqRef = doc(firestoreDb, "allInquiries", selectedInquiry.id);
      const consultant = consultants.find((c) => {
          const email = c.email || c.user_email || c.owner_email || c.login;
          return email === selectedConsultantEmail;
        }) || {};

      const consultantEmail = selectedConsultantEmail;
      const consultantName = consultant.name || consultant.full_name || consultant.display_name || (consultantEmail ? consultantEmail.split("@")[0] : "Unknown");

      await updateDoc(inqRef, {
        consultantEmail,
        consultantName,
        assignedConsultantEmail: consultantEmail,
        assignedBy: user?.email || "superadmin",
        assignedAt: serverTimestamp(),
        status: selectedInquiry.status && selectedInquiry.status !== "new_chat" ? selectedInquiry.status : "pending_consultant",
      });

      setSnackbarMsg("Inquiry assigned to consultant.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleCloseAssignDialog();
    } catch (err) {
      console.error("Error assigning inquiry", err);
      setSnackbarMsg("Failed to assign inquiry.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleAssignTaskForInquiry = useCallback((inq) => {
    if (!inq) return;
    const fullName = inq.formData?.fullName || "Customer";
    const subject = inq.formData?.subject || inq.formData?.serviceType || "";
    const baseDesc = `Follow up on inquiry ${inq.id} for ${fullName}${subject ? ` – ${subject}` : ""}`;

    setNewTaskDescription(baseDesc);
    setNewTaskRelatedInquiryId(inq.id || "");

    const inqConsultantEmail = inq.consultantEmail || inq.consultant_email || inq.assignedConsultantEmail;
    setNewTaskTarget("consultants");
    setNewTaskAssigneeEmail(inqConsultantEmail || "");
    setSelectedMenu("Tasks & History");
    setSnackbarMsg("Task form pre-filled from inquiry. Review and click 'Create Task'.");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
  }, []);

  const handleMarkTaskAsCompleted = useCallback(async (taskId) => {
    if (!taskId) return;
    try {
      const taskRef = doc(firestoreDb, "superadminTasks", taskId);
      await updateDoc(taskRef, {
        status: "completed",
        completedAt: serverTimestamp(),
        completedBy: user?.email || user?.id || "superadmin",
      });

      const current = tasks.find((t) => t.id === taskId);
      const consultantTaskId = current?.consultantTaskId;
      if (consultantTaskId) {
        const consultantRef = doc(firestoreDb, "consultantTasks", consultantTaskId);
        await updateDoc(consultantRef, {
          status: "completed",
          completedAt: serverTimestamp(),
        });
      }
      setSnackbarMsg("Task marked as completed.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error marking task complete", err);
      setSnackbarMsg("Failed to mark task as completed.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [tasks, user]);

  const handleCreateTask = async () => {
    if (!newTaskDescription.trim()) return;
    try {
      const createdById = user?.uid || user?.email || user?.id || "superadmin";
      const createdByName = user?.name || user?.displayName || (user?.email ? user.email.split("@")[0] : "SuperAdmin");
      const rawEmail = newTaskAssigneeEmail.trim();
      const trimmedEmail = rawEmail.toLowerCase();
      let consultantId = null;

      if (trimmedEmail) {
        try {
          const usersRef = collection(firestoreDb, "users");
          const qUsers = query(usersRef, where("email", "==", trimmedEmail));
          const snap = await getDocs(qUsers);
          if (!snap.empty) {
            const userDoc = snap.docs[0];
            const data = userDoc.data() || {};
            consultantId = data.uid || userDoc.id;
          }
        } catch (e) {
          console.error("Error resolving consultantId from users collection", e);
        }
      }

      if (!consultantId && trimmedEmail) {
        consultantId = trimmedEmail;
      }

      const consultantTarget = newTaskTarget === "consultants" || newTaskTarget === "marketing" ? newTaskTarget : "";
      const scope = rawEmail ? "individual" : newTaskTarget === "all" ? "all" : "department";

      const commonFields = {
        taskDescription: newTaskDescription.trim(),
        assigneeEmail: trimmedEmail || null,
        relatedInquiryId: newTaskRelatedInquiryId.trim() || null,
        consultantId: consultantId || null,
      };

      const superadminRef = await addDoc(collection(firestoreDb, "superadminTasks"), {
        ...commonFields,
        target: newTaskTarget,
        status: "pending",
        createdAt: serverTimestamp(),
        createdBy: createdById,
        createdByName,
        scope,
        consultantTaskId: null,
      });

      const consultantRef = await addDoc(collection(firestoreDb, "consultantTasks"), {
        ...commonFields,
        target: consultantTarget,
        status: "pending",
        scope,
        assignedAt: serverTimestamp(),
        assignedBy: createdById,
        assignedByName: createdByName,
        superadminTaskId: superadminRef.id,
      });

      await updateDoc(superadminRef, {
        consultantTaskId: consultantRef.id,
      });

      setNewTaskDescription("");
      setNewTaskAssigneeEmail("");
      setNewTaskRelatedInquiryId("");

      setSnackbarMsg("Task created and sent to team.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error creating task", err);
      setSnackbarMsg("Failed to create task.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const confirmLogout = async () => {
    setLogoutDialogOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  // ----------------- RENDER HELPERS -----------------
  const renderOverview = () => (
    <>
      <Grid container columns={{ xs: 1, sm: 2, md: 15 }} spacing={3} alignItems="stretch" sx={{ mt: 1, width: "100%" }}>
        <Grid item xs={1} sm={1} md={3}>
          <StatCard
            title="Total Inquiries From IMS"
            value={totalInquiries}
            subtitle="All channels combined"
            icon={MailOutlineIcon}
            loading={loadingInquiries}
            onClick={() => setSelectedMenu("Inquiries")}
          />
        </Grid>
        <Grid item xs={1} sm={1} md={3}>
          <StatCard
            title="Require attention"
            value={overdueInquiriesCount}
            subtitle="Overdue inquiries"
            icon={NotificationsActiveIcon}
            color="secondary"
            loading={loadingInquiries}
            onClick={() => setSelectedMenu("Inquiries")}
          />
        </Grid>
        <Grid item xs={1} sm={1} md={3}>
          <StatCard
            title="Response rate"
            value={Math.round(globalResponseRate || 0)}
            subtitle="Global response rate"
            icon={QueryStatsIcon}
            color="primary"
            loading={loadingInquiries}
            onClick={undefined}
          />
        </Grid>
        <Grid item xs={1} sm={1} md={3}>
          <StatCard
            title="Email Engagement"
            displayValue={`${emailEngagement.openRate}%`}
            subtitle={`Interaction rate ${emailEngagement.clickRate}% · ${emailEngagement.opened.toLocaleString()} opens`}
            icon={MailOutlineIcon}
            color="primary"
            loading={emailEngagementLoading}
            onClick={() => setSelectedMenu("Performance")}
          />
        </Grid>
        <Grid item xs={1} sm={1} md={3}>
          <StatCard
            title="Team Tasks"
            value={tasks.filter((t) => t.status !== "completed").length}
            subtitle="Active tasks"
            icon={AssignmentTurnedInIcon}
            color="secondary"
            loading={tasksLoading}
            onClick={() => setSelectedMenu("Tasks & History")}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2.5, width: "100%" }}>
        <BroadcastBudgetPanel
          loading={broadcastBudgetLoading}
          channels={broadcastBudgetChannels}
          subtitle="Email and WhatsApp budgets are enforced by the same broadcast guard."
        />
      </Box>

      <Grid container spacing={3} alignItems="stretch" sx={{ mt: 2, width: "100%" }}>
        {/* Google Contacts */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            component={motion.div}
            whileHover={{ y: -3, boxShadow: "0 18px 38px rgba(15,23,42,0.09)" }}
            sx={{
              width: "100%",
              p: 3,
              height: "100%",
              borderRadius: 4,
              background: "linear-gradient(135deg,#ecfdf3,#ffffff)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                Google Contacts
              </Typography>
              <Chip size="small" label="Live" color="success" variant="filled" sx={{ borderRadius: 999, pointerEvents: "none" }} />
            </Box>
            <SpeedTestDial loading={gcLoading} value={googleContactsCount} />
             <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary", textAlign: "center" }}>
                contacts
            </Typography>
          </Paper>
        </Grid>

        {/* HubSpot Contacts */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            component={motion.div}
            whileHover={{ y: -3, boxShadow: "0 18px 38px rgba(15,23,42,0.09)" }}
            sx={{
              width: "100%",
              p: 3,
              height: "100%",
              borderRadius: 4,
              background: "linear-gradient(135deg,#eff6ff,#ffffff)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                HubSpot Contacts
              </Typography>
              <Chip size="small" label="Live" color="success" variant="filled" sx={{ borderRadius: 999, pointerEvents: "none" }} />
            </Box>
            <SpeedTestDial loading={hubspotLoading} value={hubspotContactsCount} />
             <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary", textAlign: "center" }}>
                contacts
            </Typography>
          </Paper>
        </Grid>

        {/* Top Email Senders */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ width: "100%", p: 3, borderRadius: 2, height: "100%", minHeight: 300 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Top Email Senders</Typography>
              <Tooltip title="Based on SendGrid / email campaign logs">
                <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
            {emailStatsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
            ) : emailSenders.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>No email campaigns found yet.</Typography>
            ) : (
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 320 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Emails Sent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emailSenders.map((row, idx) => {
                      const email = row.user_email || row.sender_email || row.email || row.owner_email || "";
                      const name = row.user_name || row.sender_name || row.name || (email ? email.split("@")[0] : "Unknown");
                      const rawCount = row.total_sent ?? row.sent_count ?? row.emails_sent ?? row.total ?? 0;
                      const count = typeof rawCount === "number" ? rawCount : parseInt(rawCount || "0", 10);
                      return (
                        <TableRow key={email || name || idx}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{name}</Typography>
                            {email && <Typography variant="caption" sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>{email}</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={count.toLocaleString()} size="small" color="primary" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </>
  );

  const memoizedInquiries = useMemo(() => {
    return (
        <Paper sx={{ width: "100%", p: { xs: 2, md: 3 }, borderRadius: 4, height: "100%", overflow: 'hidden' }}>
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>All Inquiries ({totalInquiries})</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Same structure as your Admin &gt; Inquiries page – but from SuperAdmin.
              </Typography>
            </Box>
            <Chip color="warning" label={`${overdueInquiriesCount} overdue`} size="small" />
          </Box>

          {loadingInquiries ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
          ) : totalInquiries === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>No inquiries found.</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: "65vh", overflowX: "auto" }}>
              <Table stickyHeader size="small" sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Consultant</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allInquiries.map((inq) => {
                    const isOverdue = !!inq.isOverdue;
                    const isClosed = inq.status === "closed_by_consultant" || inq.status === "closed_by_user" || inq.status === "closed_by_provider";
                    return (
                      <TableRow key={inq.id} hover sx={{ bgcolor: isOverdue ? "rgba(239,68,68,0.04)" : "transparent" }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{inq.formData?.fullName || "Unknown"}</Typography>
                          {inq.formData?.email && <Typography variant="caption" sx={{ color: "text.secondary" }}>{inq.formData.email}</Typography>}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={inq.status?.replace(/_/g, " ") || "N/A"} color={isClosed ? "default" : isOverdue ? "warning" : "info"} sx={{ textTransform: "capitalize", pointerEvents: "none" }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDateTime(inq.createdAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{inq.consultantName || "Unassigned"}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
                            <Button variant="outlined" size="small" onClick={() => handleOpenAssignDialog(inq)}>Assign</Button>
                            {isOverdue && (
                              <Tooltip title="Assign follow-up task">
                                  <IconButton size="small" onClick={() => handleAssignTaskForInquiry(inq)}>
                                    <TaskAltIcon fontSize="small" color="warning" />
                                  </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
    );
  }, [allInquiries, totalInquiries, overdueInquiriesCount, loadingInquiries, handleOpenAssignDialog, handleAssignTaskForInquiry]);

  const memoizedTaskTable = useMemo(() => {
    const pending = tasks.filter((t) => t.status !== "completed");
    const completed = tasks.filter((t) => t.status === "completed");

    return (
        <Paper sx={{ width: "100%", p: { xs: 2, md: 3 }, borderRadius: 4, height: "100%", overflow: 'hidden' }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Task History</Typography>
              <Chip size="small" color="primary" label={`${pending.length} open / ${completed.length} completed`} sx={{ pointerEvents: "none" }} />
            </Box>

            {tasksLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
            ) : tasks.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>No tasks created yet.</Typography>
            ) : (
              <TableContainer sx={{ maxHeight: "60vh", overflowX: "auto" }}>
                <Table size="small" stickyHeader sx={{ minWidth: 560 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell>Target</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasks.map((task) => (
                      <TableRow key={task.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{task.taskDescription}</Typography>
                          {task.relatedInquiryId && (
                             <Typography variant="caption" sx={{ color: "text.secondary" }}>Inquiry: {task.relatedInquiryId}</Typography>
                          )}
                          {task.assigneeEmail && <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>Assigned to: {task.assigneeEmail}</Typography>}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={task.target || "N/A"} variant="outlined" sx={{ pointerEvents: "none" }} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={task.status || "pending"} color={task.status === "completed" ? "success" : "warning"} sx={{ textTransform: "capitalize", pointerEvents: "none" }} />
                        </TableCell>
                        <TableCell>
                           <Typography variant="caption">{formatDateTime(task.createdAt)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          {task.status !== "completed" && (
                            <Tooltip title="Mark completed">
                                <IconButton size="small" onClick={() => handleMarkTaskAsCompleted(task.id)}>
                                <TaskAltIcon fontSize="small" color="success" />
                                </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
        </Paper>
    );
  }, [tasks, tasksLoading, handleMarkTaskAsCompleted]);

  const renderTasks = () => {
    const targetPool = newTaskTarget === "consultants" ? consultants : newTaskTarget === "marketing" ? marketingUsers : [...consultants, ...marketingUsers];
    const targetLabel = newTaskTarget === "consultants" ? "consultant" : newTaskTarget === "marketing" ? "marketing user" : "user";

    return (
      <Stack spacing={3} sx={{ width: '100%', mt: 1 }}>
        <Paper sx={{ p: 3, borderRadius: 4, display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, mt: 3 }}>Assign Task</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Assign work to Marketing or Consultants, or link it directly to a specific overdue inquiry.
            </Typography>

            <TextField
              label="Task description"
              value={newTaskDescription}
              onChange={(e) => setNewTaskDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ mb: 4, mt: 2 }}
            />

            <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5, color: "text.secondary" }}>
                        Target Group
                    </Typography>
                    <Select
                        fullWidth
                        value={newTaskTarget}
                        onChange={(e) => {
                        setNewTaskTarget(e.target.value);
                        setNewTaskAssigneeEmail("");
                        }}
                    >
                        <MenuItem value="consultants">Consultants</MenuItem>
                        <MenuItem value="marketing">Marketing</MenuItem>
                        <MenuItem value="all">All (Marketing + Consultants)</MenuItem>
                    </Select>
                 </Grid>
                 
                 <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5, color: "text.secondary" }}>
                        Specific {targetLabel} (optional)
                    </Typography>
                    <Select
                        fullWidth
                        displayEmpty
                        value={newTaskAssigneeEmail}
                        onChange={(e) => setNewTaskAssigneeEmail(e.target.value)}
                        disabled={usersLoading}
                    >
                        <MenuItem value=""><em>All {targetLabel}s</em></MenuItem>
                        {targetPool.map((u, idx) => {
                        const email = u.email || u.user_email || u.owner_email || u.login;
                        const name = u.name || u.full_name || u.display_name || (email ? email.split("@")[0] : "Unknown");
                        if (!email) return null;
                        return <MenuItem key={email || idx} value={email}>{name} ({email})</MenuItem>;
                        })}
                    </Select>
                 </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleCreateTask}
                    disabled={!newTaskDescription.trim()}
                    sx={{ width: 350, maxWidth: '100%', py: 1.2 }}
                >
                    Create Task
                </Button>
            </Box>
        </Paper>

        {memoizedTaskTable}

      </Stack>
    );
  };

  if (authLoading && !user) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h5">Access denied</Typography>
        <Typography>Please log in as SuperAdmin.</Typography>
        <Button variant="contained" href="/login" sx={{ mt: 2 }}>Go to Login</Button>
      </Box>
    );
  }

  const menus = [
    { label: "Command Center", icon: RadarIcon },
    { label: "Overview", icon: DashboardIcon },
    { label: "Inquiries", icon: MailOutlineIcon },
    { label: "Phone Rotations", icon: MailOutlineIcon, action: "open-phone-rotations" },
    { label: "Tasks & History", icon: AssignmentTurnedInIcon },
    { label: "Performance", icon: PeopleAltIcon },
  ];

  const renderContent = () => {
    switch (selectedMenu) {
      case "Command Center":
        return (
           <Box sx={{ p: 0, height: '100%' }}>
              <AICoachWidget 
                  inquiries={allInquiries}
                  user={user} 
                  hotLeads={allInquiries.map(inq => ({
                      ...inq,
                      score: (inq.isUrgent || inq.priority === 'high' || inq.leadStatus === 'hot') ? 85 : 15
                  }))} 
                  onAlertClick={() => setSelectedMenu("Inquiries")} 
                  onAssignClick={handleWidgetAssignClick} 
                  // onRemindClick={handleRemindConsultant} // Ensure this prop is passed if the widget uses it
              />
           </Box>
        );
      case "Inquiries": return memoizedInquiries; 
      case "Tasks & History": return renderTasks();
      case "Performance": return <Performance />;
      case "Overview": default: return renderOverview();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        minHeight: 0,
        minWidth: 0,
        bgcolor: "#f4f5f7",
        overflow: "hidden",
        "& .MuiChip-root": {
          pointerEvents: "none",
          cursor: "default",
        },
      }}
    >
      <AppBar position="fixed" sx={{ zIndex: superAdminTheme.zIndex.drawer + 1, ml: { sm: `${DRAWER_WIDTH}px` }, width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
        <Toolbar sx={{ height: HEADER_HEIGHT, gap: 1 }}>
          {!isDesktop && (
            <IconButton edge="start" onClick={handleDrawerToggle} sx={{ mr: { xs: 0.5, sm: 2 } }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, minWidth: 0, fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1.25rem" }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            SuperAdmin · {selectedMenu}
          </Typography>
          <Tooltip title="Notifications">
            <IconButton sx={{ flexShrink: 0 }}>
              <Badge badgeContent={notificationsCount} color="error">
                <NotificationsActiveIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Box sx={{ ml: { xs: 0.5, sm: 2 }, display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main", mr: 1, fontWeight: 600 }}>
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            {isDesktop && <Typography variant="body2" sx={{ mr: 2 }}>{displayName}</Typography>}
            {isDesktop ? (
              <Button variant="outlined" size="small" onClick={() => setLogoutDialogOpen(true)} startIcon={<LogoutIcon />}>
                  Logout
              </Button>
            ) : (
              <Tooltip title="Logout">
                <IconButton onClick={() => setLogoutDialogOpen(true)} size="small">
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isDesktop ? "permanent" : "temporary"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
        }}
      >
        <Toolbar sx={{ height: HEADER_HEIGHT }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box component="img" src="/pics/logo.png" alt="Logo" sx={{ height: 32 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>SuperAdmin</Typography>
          </Box>
        </Toolbar>
        <Divider />
        <List sx={{ mt: 1 }}>
          {menus.map(({ label, icon: Icon, action }) => (
            <ListItemButton
              key={label}
              selected={selectedMenu === label}
              onClick={() => {
                if (action === "open-phone-rotations") {
                  navigate("/omnichannel/phone-rotations", { state: { from: "/superadmin" } });
                  if (!isDesktop) setDrawerOpen(false);
                  return;
                }
                setSelectedMenu(label);
                if (!isDesktop) setDrawerOpen(false);
              }}
              sx={{
                mx: 1.5, mb: 0.5, borderRadius: 3,
                "&.Mui-selected": {
                  bgcolor: "primary.main", color: "primary.contrastText",
                  "&:hover": { bgcolor: "primary.dark" },
                  "& .MuiSvgIcon-root": { color: "primary.contrastText" },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}><Icon /></ListItemIcon>
              <ListItemText primary={label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minHeight: 0, minWidth: 0, width: "100%", p: { xs: 1.5, sm: 3 }, pt: { xs: 10, sm: 11 }, overflowY: "auto" }}>
        <AnimatePresence mode="wait">
          <motion.div key={selectedMenu} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }} style={{ height: "100%", minHeight: 0, width: "100%" }}>
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </Box>

      <Dialog open={assignDialogOpen} onClose={handleCloseAssignDialog}>
        <DialogTitle>Assign Inquiry</DialogTitle>
        <DialogContent>
          <DialogContentText>You are assigning this inquiry to a consultant.</DialogContentText>
          <Typography sx={{ fontWeight: 600, mt: 1 }}>{selectedInquiry?.formData?.fullName}</Typography>
          <Select fullWidth sx={{ mt: 2 }} value={selectedConsultantEmail} onChange={(e) => setSelectedConsultantEmail(e.target.value)} displayEmpty>
            <MenuItem value=""><em>Select consultant</em></MenuItem>
            {consultants.map((c) => {
              const email = c.email || c.user_email || c.owner_email || c.login;
              const name = c.name || c.full_name || c.display_name || (email ? email.split("@")[0] : "Unknown");
              if (!email) return null;
              return <MenuItem value={email} key={email}>{name} ({email})</MenuItem>;
            })}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog}>Cancel</Button>
          <Button variant="contained" onClick={assignInquiry} disabled={!selectedConsultantEmail}>Assign</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
        <DialogTitle>Log out?</DialogTitle>
        <DialogContent><DialogContentText>You will be logged out of the SuperAdmin dashboard.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmLogout} color="error" autoFocus>Logout</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)} sx={{ borderRadius: 2, boxShadow: 4 }}>{snackbarMsg}</Alert>
      </Snackbar>
    </Box>
  );
}

export default function SuperAdminDashboard() {
  return (
    <ThemeProvider theme={superAdminTheme}>
      <CssBaseline />
      <SuperAdminDashboardInner />
    </ThemeProvider>
  );
}
